import { UnVerified, nameRegex } from 'constants/teacherRecruitment';
import { Mui } from '@onedesign/ui';
import symbol from '/static/images/symbolLogin.svg';
import { useLoadingService } from 'services/LoadingService';
import { useLoginService } from 'services/LoginService';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ILoginData } from 'types/login';
import { t } from 'utils/i18n';
import { Icon, SvgName } from 'components/Icon';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'routes/routeConfig';
import { ERemoteConfigParameter } from 'services/FeatureToggleService/remoteConfigService.constants';
import { useFeatureToggle } from 'hooks/index';
import { useState } from 'react';
import UnverifiedAccount from 'pages/Login/UnverifiedAccount';
import {
  StyledFormContainer,
  StyledFormControl,
  StyledLoginMain,
  StyledLoginSymbol,
  StyledLoginSymbolContainer,
  StyledSwitchToRegisterAndResetPasswordRequestBox,
  StyledSwitchTypography,
} from './Login.style';

const { Button, TextField, FormHelperText, IconButton, InputAdornment } = Mui;
const TOKEN_SOURCE = 'Nani';

const Login = () => {
  const schema = yup
    .object({
      account: yup
        .string()
        .required(t('login.validation.accountRequired', '帳號未輸入'))
        .matches(
          nameRegex,
          t(
            'login.validation.accountFormatInvalid',
            '會員帳號格式錯誤、第一個字限定英文',
          ),
        )
        .min(
          6,
          t(
            'login.validation.accountTooShort',
            '會員帳號格式錯誤、限定六到三十字元',
          ),
        )
        .max(
          30,
          t(
            'login.validation.accountTooLong',
            '會員帳號格式錯誤、限定六到三十字元',
          ),
        ),
      password: yup
        .string()
        .required(t('login.validation.passwordRequired', '密碼未輸入'))
        .matches(
          nameRegex,
          t(
            'login.validation.passwordFormatInvalid',
            '請輸入8-12位元英數混和密碼',
          ),
        )
        .min(
          8,
          t('login.validation.passwordTooShort', '請輸入8-12位元英數混和密碼'),
        )
        .max(
          12,
          t('login.validation.passwordTooLong', '請輸入8-12位元英數混和密碼'),
        ),
    })
    .required();
  const [enabledFeatures] = useFeatureToggle();
  const isShowTeacherRegistration = enabledFeatures(
    ERemoteConfigParameter.TEACHER_REGISTER,
  );
  const [showPassword, setShowPassword] = useState(false);
  const { showLoading, hideLoading } = useLoadingService();
  const {
    login,
    dataCheckEmailExist,
    unverifiedAccountInfo,
    clearUnverifiedAccount,
  } = useLoginService();
  const isShowUnverifiedAccount =
    dataCheckEmailExist === UnVerified && !!unverifiedAccountInfo;
  const navigate = useNavigate();
  const handleClickShowPassword = () => setShowPassword(show => !show);
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };
  const methods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      account: '',
      password: '',
    },
  });
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = methods;

  const passwordMessage = t(
    'login.validation.passwordMessage',
    '請輸入8-12英數字，大小寫視為不同',
  );

  const handleSubmitForm = async ({
    account = '',
    password = '',
  }: ILoginData) => {
    if (account && password) {
      showLoading();
      await login({ username: account, password, from: TOKEN_SOURCE });
      hideLoading();
    }
  };

  return (
    <>
      {isShowUnverifiedAccount && unverifiedAccountInfo && (
        <UnverifiedAccount
          account={unverifiedAccountInfo.account}
          email={unverifiedAccountInfo.email}
          onClear={clearUnverifiedAccount}
        />
      )}

      <StyledLoginMain>
        <h2>{t('login.teacherLogin', '教師登入')}</h2>

        <FormProvider {...methods}>
          <StyledFormContainer>
            <form>
              <StyledFormControl>
                <Controller
                  name="account"
                  control={control}
                  render={({ field }) => {
                    return (
                      <>
                        <TextField
                          {...field}
                          placeholder={t(
                            'login.accountPlaceholder',
                            '教師帳號',
                          )}
                          fullWidth
                          error={!!errors.account}
                        />
                        {errors.account && (
                          <FormHelperText
                            sx={{
                              color: theme => theme.palette.error.main,
                            }}
                          >
                            {errors.account.message}
                          </FormHelperText>
                        )}
                      </>
                    );
                  }}
                />
              </StyledFormControl>
              <StyledFormControl>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <>
                      <TextField
                        {...field}
                        type={showPassword ? 'text' : 'password'}
                        placeholder={t('login.passwordPlaceholder', '教師密碼')}
                        fullWidth
                        error={!!errors.password}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                              >
                                {showPassword ? (
                                  <Icon name={SvgName.EyeOn} />
                                ) : (
                                  <Icon name={SvgName.EyeOff} />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {errors.password ? (
                        <FormHelperText
                          sx={{
                            color: theme => theme.palette.error.main,
                          }}
                        >
                          {errors.password.message}
                        </FormHelperText>
                      ) : (
                        <FormHelperText
                          sx={{
                            color: theme => theme.palette.grey[600],
                          }}
                        >
                          {passwordMessage}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </StyledFormControl>
              <Button
                type="submit"
                id="btn-login"
                sx={{ padding: '12px 0', marginTop: '2rem' }}
                variant="contained"
                onClick={handleSubmit(data => handleSubmitForm(data))}
              >
                {t('login.loginButton', '登入')}
              </Button>
            </form>
          </StyledFormContainer>
        </FormProvider>
        {isShowTeacherRegistration && (
          <StyledSwitchToRegisterAndResetPasswordRequestBox>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <StyledSwitchTypography
                onClick={() => navigate(PATH.REGISTER, { replace: true })}
              >
                {t('login.callToRegisterButton', 'Sign up')}
              </StyledSwitchTypography>
              <Icon
                sx={{
                  width: '14px',
                  height: '14px',
                  marginLeft: '12px',
                  marginRight: '12px',
                }}
                name={SvgName.Divider}
              />

              <StyledSwitchTypography
                onClick={() => navigate(PATH.RESET_PASSWORD_REQUEST)}
              >
                {t('login.callToResetPasswordRequestButton', 'Reset Password?')}
              </StyledSwitchTypography>
            </Box>
          </StyledSwitchToRegisterAndResetPasswordRequestBox>
        )}
      </StyledLoginMain>

      <StyledLoginSymbolContainer>
        <StyledLoginSymbol src={symbol} />
      </StyledLoginSymbolContainer>
    </>
  );
};

export default Login;
